/**
 * Hook: same display logs as /logs after search, permission, time,
 * compliance, type checks, and cash/units filters.
 */

import { useMemo } from 'react';
import { useAppSelector } from 'redux/hooks';
import { selectPermission, selectStaffId, selectUsername } from 'redux/global';
import { selectCustomers, selectUsers } from 'pages/Settings/redux';
import { selectErpRecords } from 'pages/Records/redux';
import { useSettings } from 'pages/Settings/hooks';
import { useLogs } from './hooks';
import {
  selectAssociateElevatedActivityLogs,
  selectActivityLogsTypeAttributeMode,
  selectActivityLogsViewAs,
  selectActivityLogsOnlyLogIds,
  selectActivityLogsStartLogId,
  selectActivityLogsEndLogId,
  selectComplianceActivityStaffId,
  selectIsComplianceActivity,
  selectLogSearch,
  selectLogSearchOptions,
  selectLogSearches,
  selectLogsChecked,
  selectLogsValueMode,
  selectSingleLogTime,
  selectActivityLogsUseTimeRange,
  selectActivityLogsAllItems,
  selectActivityLogsCashIn,
  selectActivityLogsCashOut,
} from './redux';
import { buildCustomerMaps } from './logCustomer';
import { buildStaffMaps } from './logActor';
import {
  ActivityLogsDisplayLogsParams,
  buildActivityLogsDisplayLogs,
} from './activityLogsDisplay';
import type { ActivityLogsTypeAttributeMode } from './logUtils';

export type UseActivityLogsDisplayLogsOptions = {
  /** Override ActivityLogs cash/units toggles (default = live Redux /logs settings). */
  allItems?: boolean,
  cashIn?: boolean,
  cashOut?: boolean,
  /** Force time on/off; default = singleLogTime has a range (ActivityLogs time toggle). */
  useTimeRange?: boolean,
  /** Override checked types; default = Redux ActivityLogs checks. */
  checked?: string[],
  /** Reporting composer: ignore leftover Show-logs id bounds from /logs jumps. */
  skipLogIdBounds?: boolean,
};

/**
 * Returns the exact log set /logs would show (pre-pagination) for the
 * current Redux ActivityLogs filters + session permission.
 */
export const useActivityLogsDisplayLogs = (options: UseActivityLogsDisplayLogsOptions = {}) => {
  const permission = useAppSelector(selectPermission);
  const staffId = useAppSelector(selectStaffId);
  const username = useAppSelector(selectUsername);
  const activityLogsViewAs = useAppSelector(selectActivityLogsViewAs);
  const activityLogsOnlyLogIds = useAppSelector(selectActivityLogsOnlyLogIds);
  const activityLogsStartLogId = useAppSelector(selectActivityLogsStartLogId);
  const activityLogsEndLogId = useAppSelector(selectActivityLogsEndLogId);
  // Shared report: use the author's access so a lower-perm recipient sees the same logs.
  const effectivePermission = activityLogsViewAs
    ? (Number(activityLogsViewAs.permission) || 0)
    : (Number(permission) || 0);
  const effectiveStaffId = activityLogsViewAs ? activityLogsViewAs.staffId : staffId;
  const effectiveUsername = activityLogsViewAs ? activityLogsViewAs.username : username;
  const { users: settingsUsers } = useSettings();
  const usersFromStore = useAppSelector(selectUsers);
  const users = settingsUsers?.length ? settingsUsers : (usersFromStore || []);
  const erpRecords = useAppSelector(selectErpRecords);
  const customers = useAppSelector(selectCustomers);
  const logSearch = useAppSelector(selectLogSearch);
  const logSearchOptions = useAppSelector(selectLogSearchOptions);
  const logSearches = useAppSelector(selectLogSearches);
  const logsChecked = useAppSelector(selectLogsChecked);
  const logsValueMode = useAppSelector(selectLogsValueMode);
  const activityLogsTypeAttributeMode = useAppSelector(selectActivityLogsTypeAttributeMode) as ActivityLogsTypeAttributeMode;
  const singleLogTime = useAppSelector(selectSingleLogTime);
  const activityLogsUseTimeRange = useAppSelector(selectActivityLogsUseTimeRange);
  const activityLogsAllItems = useAppSelector(selectActivityLogsAllItems);
  const activityLogsCashIn = useAppSelector(selectActivityLogsCashIn);
  const activityLogsCashOut = useAppSelector(selectActivityLogsCashOut);
  const associateElevatedActivityLogs = useAppSelector(selectAssociateElevatedActivityLogs);
  const isComplianceActivityFlag = useAppSelector(selectIsComplianceActivity);
  const complianceActivityStaffId = useAppSelector(selectComplianceActivityStaffId);

  const {
    logs,
    logGroups,
    activityGroupsModel,
    deletedDepartmentNames,
    deletedInventoryItemNames,
    deletedInventoryGroupNames,
  } = useLogs();

  const skipLogIdBounds = Boolean(options.skipLogIdBounds);
  const checked = options.checked ?? (logsChecked || []);
  const valueMode = (logsValueMode === 'units' ? 'units' : 'value') as 'value' | 'units';
  // Time is an explicit toggle (activityLogsUseTimeRange). A leftover singleLogTime
  // from an earlier visit must not shrink Reporting when Time is off.
  const useTimeRange = options.useTimeRange ?? Boolean(
    activityLogsUseTimeRange
    && singleLogTime?.startDate
    && singleLogTime?.endDate,
  );
  // ActivityLogs Time only — do not borrow Metrics' range (that would shrink report sets).
  const { startDate, endDate } = (singleLogTime || {}) as {
    startDate?: string,
    endDate?: string,
  };

  // Same formula as LogTable (elevate unlocks higher-role view; compliance filter is separate).
  const isComplianceActivity = Boolean(
    isComplianceActivityFlag
    && useTimeRange
    && startDate
    && endDate
    && complianceActivityStaffId,
  );

  // Live /logs cash toggles — Reporting and Metrics read the same Redux values.
  const allItems = options.allItems ?? activityLogsAllItems;
  const cashIn = options.cashIn ?? activityLogsCashIn;
  const cashOut = options.cashOut ?? activityLogsCashOut;

  const staffMaps = useMemo(() => buildStaffMaps(users), [users]);
  const customerMaps = useMemo(() => buildCustomerMaps(customers), [customers]);
  const departmentNames = useMemo(
    () => new Set(logGroups.Departments || []),
    [logGroups.Departments],
  );
  const searchArchives = useMemo(() => ({
    deletedTypes: deletedInventoryItemNames,
    deletedGroups: deletedInventoryGroupNames,
    deletedDepartments: deletedDepartmentNames,
  }), [deletedInventoryItemNames, deletedInventoryGroupNames, deletedDepartmentNames]);

  const params: ActivityLogsDisplayLogsParams = {
    logs: logs || [],
    permission: effectivePermission,
    staffId: effectiveStaffId,
    username: effectiveUsername,
    users: users || [],
    erpRecords: erpRecords || [],
    logSearches: logSearches || [],
    logSearch: logSearch || '',
    logSearchOptions,
    staffMaps,
    customerMaps,
    departmentNames,
    searchArchives,
    checked,
    logGroups: logGroups as Record<string, string[]>,
    activityGroupsModel,
    useTimeRange,
    startDate,
    endDate,
    valueMode,
    allItems,
    cashIn,
    cashOut,
    associateElevatedActivityLogs: Boolean(associateElevatedActivityLogs),
    isComplianceActivity,
    complianceActivityStaffId,
    onlyLogIds: skipLogIdBounds ? null : activityLogsOnlyLogIds,
    startLogId: skipLogIdBounds ? null : activityLogsStartLogId,
    endLogId: skipLogIdBounds ? null : activityLogsEndLogId,
  };

  const displayLogs = useMemo(
    () => buildActivityLogsDisplayLogs(params),
    [
      logs,
      effectivePermission,
      effectiveStaffId,
      effectiveUsername,
      users,
      erpRecords,
      logSearches,
      logSearch,
      logSearchOptions,
      staffMaps,
      customerMaps,
      departmentNames,
      searchArchives,
      checked,
      logGroups,
      activityGroupsModel,
      useTimeRange,
      startDate,
      endDate,
      valueMode,
      allItems,
      cashIn,
      cashOut,
      associateElevatedActivityLogs,
      isComplianceActivity,
      complianceActivityStaffId,
      activityLogsUseTimeRange,
      skipLogIdBounds,
      activityLogsOnlyLogIds,
      activityLogsStartLogId,
      activityLogsEndLogId,
    ],
  );

  return {
    displayLogs,
    logGroups,
    activityGroupsModel,
    checked,
    valueMode,
    attributeMode: activityLogsTypeAttributeMode,
    useTimeRange,
    startDate,
    endDate,
    allItems,
    cashIn,
    cashOut,
  };
};

export default useActivityLogsDisplayLogs;
